/**
 * Navigation of the SQUAD editor form.
 *
 * The form is grouped in the order the PDF reads: one group per page and column, so an
 * editor who sees something on page 2 left finds its field under "Seite 2 – links". The
 * MiFID overlay is optional and rarely touched, so it sits last (Benjamin, 05.10.2026).
 *
 * Jumping: a rail of circles in the free strip left of the form — A, S1 … S4, M — one per
 * page rather than per column (05.10.2026). The circle of the page in view is highlighted.
 */

/**
 * CIXON CD (Style Guide „CD-Grundlagen“, Juli 2022) for the editor chrome — rail and group
 * headings. Pink and Purple for type and surfaces, the Pink→Purple gradient for surfaces
 * only. The app theme is close but not exact (secondary #BF0A76, info #67378D), so the
 * values are taken from the guide here.
 */
export const CIXON_CD = {
  pink: '#cb0774',
  purple: '#54267F',
  middleGrey: '#52646c',
  darkGrey: '#003c50',
  gradient: 'linear-gradient(135deg, #cb0774 0%, #54267F 100%)',
} as const;

export const SQUAD_FORM_GROUPS = [
  { key: 'allgemein', title: 'Allgemein', hint: 'Gilt für das ganze Factsheet' },
  { key: 's1-links', title: 'Seite 1 – links', hint: 'Seitenleiste' },
  { key: 's1-rechts', title: 'Seite 1 – rechts' },
  { key: 's2-links', title: 'Seite 2 – links', hint: 'Seitenleiste' },
  { key: 's2-rechts', title: 'Seite 2 – rechts', hint: 'Portfolio' },
  { key: 's3-links', title: 'Seite 3 – links', hint: 'Seitenleiste' },
  { key: 's3-rechts', title: 'Seite 3 – rechts' },
  { key: 's4', title: 'Seite 4', hint: 'Erläuterungen und Hinweise' },
  { key: 'mifid', title: 'MiFID-Overlay', hint: 'Optional: Wertentwicklung unter 12 Monaten abdecken' },
] as const;

export type SquadFormGroupKey = (typeof SQUAD_FORM_GROUPS)[number]['key'];

export const squadFormGroupId = (key: SquadFormGroupKey) => `squad-form-${key}`;

/** One circle per page; `target` is where a click jumps to, `groups` what lights it up. */
export const SQUAD_FORM_RAIL: ReadonlyArray<{
  short: string;
  title: string;
  target: SquadFormGroupKey;
  groups: ReadonlyArray<SquadFormGroupKey>;
}> = [
  { short: 'A', title: 'Allgemein', target: 'allgemein', groups: ['allgemein'] },
  { short: 'S1', title: 'Seite 1', target: 's1-links', groups: ['s1-links', 's1-rechts'] },
  { short: 'S2', title: 'Seite 2', target: 's2-links', groups: ['s2-links', 's2-rechts'] },
  { short: 'S3', title: 'Seite 3', target: 's3-links', groups: ['s3-links', 's3-rechts'] },
  { short: 'S4', title: 'Seite 4', target: 's4', groups: ['s4'] },
  { short: 'M', title: 'MiFID-Overlay', target: 'mifid', groups: ['mifid'] },
];

/**
 * The group in view: the last one whose heading has passed `line` (a y position in the
 * column). Before the first heading: the first group.
 */
export function squadActiveFormGroup(
  headingTops: Array<{ key: SquadFormGroupKey; top: number }>,
  line: number,
): SquadFormGroupKey {
  let active: SquadFormGroupKey = SQUAD_FORM_GROUPS[0].key;
  for (const { key, top } of headingTops) {
    if (top <= line) active = key;
  }
  return active;
}

/**
 * Where that line sits in the column (top/bottom edges, distance still to scroll). Normally
 * 120 px below the top. The last groups are too short to scroll up to it, so on the last
 * screen the line glides down to 120 px above the bottom — every heading still gets its turn.
 */
export function squadSpyLine(top: number, bottom: number, distanceToBottom: number): number {
  const height = Math.max(bottom - top, 0);
  const sweep = Math.max(height - 240, 0);
  const progress = height > 0 ? Math.min(Math.max(1 - distanceToBottom / height, 0), 1) : 0;
  return top + 120 + sweep * progress;
}

/** The rail circle a group belongs to. */
export function squadRailItemForGroup(key: SquadFormGroupKey): string {
  const item = SQUAD_FORM_RAIL.find((r) => r.groups.indexOf(key) >= 0);
  return item ? item.short : SQUAD_FORM_RAIL[0].short;
}

/**
 * Where the rail goes: centred in the free strip between the app navigation and the form —
 * the page's left padding (100 px from 1640 px window width, 48 px on laptops). Circles up to
 * 40 px, smaller in a narrow strip; below 30 px the rail is hidden and the floating "Seiten"
 * button is the way to jump.
 */
export const SQUAD_FORM_RAIL_MAX_CIRCLE_PX = 40;
export const SQUAD_FORM_RAIL_MIN_CIRCLE_PX = 30;
export function squadRailPlacement(stripLeftPx: number, stripWidthPx: number): { left: number; size: number } | null {
  const size = Math.min(SQUAD_FORM_RAIL_MAX_CIRCLE_PX, Math.floor(stripWidthPx - 10));
  if (!(size >= SQUAD_FORM_RAIL_MIN_CIRCLE_PX)) return null;
  return { left: Math.round(stripLeftPx + (stripWidthPx - size) / 2), size };
}
